<script setup lang="ts">
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import { BreadcrumbRoute } from "~/types/breadcrumb-routes";

const FloatingList = defineAsyncComponent(() => import('~/components/molecules/FloatingList.vue'));
const GoogleRating = defineAsyncComponent(() => import('~/components/molecules/WrGoogleRating.vue'));
const WrContactUsCarouselSection = defineAsyncComponent(() => import('~/components/molecules/WrContactUsCarouselSection.vue'));
const WrExpertiseSection = defineAsyncComponent(() => import('~/components/molecules/WrExpertiseSection.vue'));
const WrHeroBanner = defineAsyncComponent(() => import('~/components/molecules/WrHeroBanner.vue'));
const WrImageContent = defineAsyncComponent(() => import('~/components/molecules/WrImageContent.vue'));
const WrOurPromiseSection = defineAsyncComponent(() => import('~/components/molecules/WrOurPromiseSection.vue'));
const WrOurServiceSection = defineAsyncComponent(() => import('~/components/molecules/WrOurServiceSection.vue'));
const WrSelectedProject = defineAsyncComponent(() => import('~/components/molecules/WrSelectedProject.vue'));
const WrWhoAreWeCard = defineAsyncComponent(() => import('~/components/molecules/WrWhoAreWeCard.vue'));
const WrDesignHeroCard = defineAsyncComponent(() => import('~/components/molecules/WrDesignHeroCard.vue'));
const WrDesignShineText = defineAsyncComponent(() => import('~/components/molecules/WrDesignShineText.vue'));
const WrDesignServiceCardsSection = defineAsyncComponent(() => import('~/components/molecules/WrDesignServiceCardsSection.vue'));
const WrTechStackSection = defineAsyncComponent(() => import('~/components/molecules/WrTechStackSection.vue'));
const WrDesignTwoByTwoCardsSection = defineAsyncComponent(() => import('~/components/molecules/WrDesignTwoByTwoCardsSection.vue'));
const WrDifferenceSection = defineAsyncComponent(() => import('~/components/molecules/WrDifferenceSection.vue'));
const WrDesignLevelUpYourCreativityWithAi = defineAsyncComponent(() => import('~/components/molecules/WrDesignLevelUpYourCreativityWithAi.vue'));
const WrDesignProcessSteps = defineAsyncComponent(() => import('~/components/molecules/WrDesignProcessSteps.vue'));
const WrDesignFaqsSection = defineAsyncComponent(() => import('~/components/molecules/WrDesignFaqsSection.vue'));
const WrDesignStatsCard = defineAsyncComponent(() => import('~/components/molecules/WrDesignStatsCard.vue'));
const WrDesignRealWorldApplicationsSection = defineAsyncComponent(() => import('~/components/molecules/WrDesignRealWorldApplicationsSection.vue'));
const WrIcon = defineAsyncComponent(() => import('~/components/global/WrIcon.vue'));

const colorMode = useColorMode();
const route = useRoute();

const url = useRequestURL();
const { data, error } = await useFetch(
  `${url.origin}/content/services/mobility/ionic-app-development-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

/* ─── SEO ─── */
const breadcrumbs = useBreadcrumbSchema(BreadcrumbRoute.Services, data.value?.hero?.heading);

useHead({
  title: data.value?.meta_tags.title || "",
  meta: data.value?.meta_tags.meta || [],
  link: data?.value?.header?.image ? [{ rel: "preload", as: "image", href: data.value.header.image }] : [],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: data.value?.hero?.heading || "Ionic App Development",
            serviceType: "Mobile App Development",
            url: `${url.origin}/services/mobility/ionic-app-development`,
            description: data.value?.hero?.subcopy || "Ionic app development services for cross-platform mobile applications.",
            provider: {
              "@type": "Organization",
              name: "WebReinvent",
              url: "https://webreinvent.com",
            },
            areaServed: {
              "@type": "Country",
              name: "India",
            },
          },
          {
            "@type": "FAQPage",
            mainEntity: (data.value?.faq?.items || []).map((item) => ({
              "@type": "Question",
              name: item.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.description.replace(/<[^>]*>/g, ""),
              },
            })),
          },
          ...(breadcrumbs.value ? [breadcrumbs.value] : []),
        ],
      }),
    },
  ],
});

const visible = ref(false);

const openModal = () => {
  visible.value = true;
};

const isLight = computed(() => colorMode.value !== 'dark');

const reasonsCards = computed(() =>
  data.value?.reasons?.items?.map((item) => ({
    svgIcon: item.svgIcon,
    title: item.title,
    desc: item.desc,
  })) || [],
);

const servicesCards = computed(() =>
  data.value?.services?.cards?.map((card) => ({
    icon: card.icon,
    title: card.title,
    desc: card.desc,
  })) || [],
);

const techStackItems = computed(() =>
  data.value?.techStack?.items?.map((item) => ({
    svg: item.svg,
    title: item.title,
    description: item.description,
    iconAppendClass: 'size-11',
  })) || [],
);

const twoByTwoCards = computed(() =>
  data.value?.twoByTwo?.items?.map((item) => ({
    id: item.id,
    imageDark: item.imageDark,
    imageLight: item.imageLight,
    imageAlt: item.imageAlt,
    title: item.title,
    description: item.description,
    textRight: item.textRight,
    rightOffset: item.rightOffset,
    descWidth: item.descWidth,
  })) || [],
);

const outcomeCards = computed(() =>
  data.value?.outcomes?.items?.map((card) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.desc,
  })) || [],
);

const ctaSteps = computed(() =>
  data.value?.cta?.cards?.map((card, i) => ({
    id: String(i + 1),
    title: card.title,
    description: card.description,
  })) || [],
);

const faqItems = computed(() =>
  data.value?.faq?.items?.map((item) => ({
    title: item.title,
    description: item.description,
    tags: item.tags || [],
  })) || [],
);

const faqFilterTags = computed(() =>
  data.value?.faq?.filterTags || [],
);

/* ─── Real-World Applications ─── */
const realWorldAppCards = computed(() => [
  {
    number: '1',
    title: 'E-Commerce & Retail',
    description: 'Product catalogs, secure checkout, and push notifications — seamless shopping on iOS and Android.',
  },
  {
    number: '2',
    title: 'Healthcare & Fitness',
    description: 'HIPAA-compliant patient portals, telemedicine, and health tracking with offline support.',
  },
  {
    number: '3',
    title: 'Education & LMS',
    description: 'Video streaming, interactive quizzes, and progress tracking for students and educators.',
  },
  {
    number: '4',
    title: 'Fintech & Banking',
    description: 'Biometric authentication, real-time transactions, and regulatory compliance for modern banking.',
  },
]);

/* ─── Ionic Advantages (Level Up) ─── */
const ionicAdvantageCards = computed(() => [
  {
    svgIcon: 'streamline:code-monitor-2',
    title: 'Single Codebase',
    description: 'Write once, deploy to iOS, Android, and the web — cutting development costs by up to 40%.',
  },
  {
    svgIcon: 'streamline:fireworks-rocket',
    title: 'Near-Native Performance',
    description: 'Smooth 60 FPS animations and responsive UX powered by modern device hardware and Capacitor plugins.',
  },
  {
    svgIcon: 'streamline:database',
    title: 'Rapid Time-to-Market',
    description: 'Ship your MVP in 4–8 weeks with reusable components, ready-made UI themes, and hot reloading.',
  },
  {
    svgIcon: 'streamline:cog',
    title: 'Framework Flexibility',
    description: 'Build with Angular, React, Vue.js, or Vanilla JavaScript — whatever your team already knows.',
  },
]);

definePageMeta({
  layout: "services-layout",
});
</script>

<template>
 
 <main class="min-h-screen px-4 lg:px-0">
    <div class="max-w-screen-xl mx-auto">

  <!-- T1: Traditional hero banner -->
  <WrHeroBanner v-if="data.header" :data="data.header" @open-modal="openModal" />

  <!-- A1: AI-style hero card -->
  <div v-if="data.hero" class="max-w-7xl px-4 mx-auto">
    <header class="pt-32">
    <WrDesignHeroCard
      :heading="data.hero.heading"
      :subcopy="data.hero.subcopy"
      :subcopy-class="data.hero.subcopyClass"
      :cta-text="data.hero.ctaText"
      :cta-link="null"
      :image-src="data.hero.imageSrc"
      :light-image-src="data.hero.lightImageSrc"
      @cta-clicked="openModal"
    />
    </header>
  </div>

  <!-- A2: Shine text -->
  <div v-if="data.shineText" class="mt-8">
    <WrDesignShineText :text="data.shineText" />
  </div>

  <!-- ============================================================ -->
  <!-- Zone 2: IMMEDIATE CREDIBILITY                                -->
  <!-- ============================================================ -->

  <!-- A3: Reasons / Key Features -->
  <WrDesignServiceCardsSection
    v-if="data.reasons"
    :heading-line1="data.reasons.headingLine1"
    :heading-line2="data.reasons.headingLine2"
    :subcopy="data.reasons.subcopy"
    :cards="reasonsCards"
    bg-image-light="/Industries-we-serve-light.svg"
    bg-image-dark="/Industries-we-serve-dark.svg"
    cta-text="Explore Capabilities"
    :cta-link="null"
    @cta-clicked="openModal"
  />

  <!-- A4: Services / Operational Signals -->
  <WrDesignServiceCardsSection
    v-if="data.services"
    :heading-line1="data.services.headingLine1"
    :heading-line2="data.services.headingLine2"
    :subcopy="data.services.subcopy"
    :cards="servicesCards"
    :bg-image-light="data.services.bgImageLight"
    :bg-image-dark="data.services.bgImageDark"
    cta-text="Explore Services"
    :cta-link="null"
    @cta-clicked="openModal"
  />

  <!-- T2: Google Autocomplete + Rating -->
  <section v-if="data.google_autocomplete" class="section-spacing dark:bg-none relative z-10 mx-auto">
    <div class="container-wrapper-xl">
      <FloatingList
        :heading="data.google_autocomplete.heading"
        :subheading="data.google_autocomplete.subheading"
        :expertisetext="data?.expertise?.expertisetext"
        :textcontent="data?.expertise?.textcontent"
        :text="data.google_autocomplete.text"
        :keywords="data.google_autocomplete.keywords"
      />
      <GoogleRating />
    </div>
  </section>

  <!-- ============================================================ -->
  <!-- Zone 3: CAPABILITIES / TECH DEPTH                            -->
  <!-- ============================================================ -->

  <!-- T3: Contact Us Carousel -->
  <section v-if="data.contact_us_carousel" class="bg-[#5F4CA4] section-spacing dark:bg-none">
    <div class="container-wrapper-xl relative z-10">
      <WrContactUsCarouselSection :data="data.contact_us_carousel" />
    </div>
  </section>

  <!-- T4: Expertise -->
  <section v-if="data.expertise" class="section-spacing dark:bg-none relative z-10">
    <div class="container-wrapper-lg">
      <WrExpertiseSection :expertise="data.expertise" />
    </div>
  </section>

  <!-- A5: Tech Stack -->
  <WrTechStackSection
    v-if="data.techStack"
    :heading="data.techStack.heading"
    :subheading="data.techStack.subheading"
    :items="techStackItems"
    cta-text="Explore the Tech Stack"
    @cta-clicked="openModal"
  />

  <!-- ============================================================ -->
  <!-- Zone 4: NARRATIVE / PROBLEM-SOLUTION                        -->
  <!-- ============================================================ -->

  <!-- T5: Who Are We -->
  <section v-if="data.service_content" class="section-spacing dark:bg-none relative z-10">
    <div class="container-wrapper-xl">
      <WrWhoAreWeCard
        :heading="data.service_content.heading"
        :subheading="data.service_content.subheading"
        :content="data.service_content.text"
      />
    </div>
  </section>

  <!-- A6: Problem / Solution (Difference) -->
  <WrDifferenceSection
    v-if="data.problem"
    :heading="data.problem.heading"
    :description="data.problem.intro"
    :left-list="{ title: data.problem.problemLabel, items: data.problem.problems }"
    :right-list="{ title: data.problem.solutionLabel, items: data.problem.solutions }"
    :quote="data.problem.quote"
    cta-text="Streamline Your Workflow"
    @cta-clicked="openModal"
  />

  <!-- A6b: Real-World Applications of Ionic -->
  <WrDesignRealWorldApplicationsSection
    :single-line-heading="'Real-World Applications of Ionic App Development'"
    :subtitle="'From e-commerce to healthcare, Ionic powers cross-platform mobile apps across every industry — delivering native-quality experiences from a single codebase.'"
    :cards="realWorldAppCards"
    cta-text="See What We Can Build"
    :cta-link="null"
    @cta-clicked="openModal"
  />

  <!-- A6c: Why Ionic — Key Advantages -->
  <WrDesignLevelUpYourCreativityWithAi
    :heading="'Why Ionic?'"
    :subcopy="'One framework, every platform — Ionic is the smart choice for cross-platform mobile development.'"
    :cards="ionicAdvantageCards"
    cta-text="Explore the Advantage"
    :cta-link="null"
    @cta-clicked="openModal"
    icon-size="40px"
  />

  <!-- ============================================================ -->
  <!-- Zone 5: PROCESS / FLOW                                       -->
  <!-- ============================================================ -->

  <!-- A7: Two By Two Cards (flow/steps) -->
 <div class="m-4"> 
  <WrDesignTwoByTwoCardsSection
    v-if="data.twoByTwo"
    :heading="data.twoByTwo.heading"
    :subcopy="data.twoByTwo.subcopy"
    :cards="twoByTwoCards"
    cta-text="See the Flow"
    :cta-link="null"
    @cta-clicked="openModal"
  >
    <template #icon-step-1>
      <WrIcon name="streamline:ticket-1" class="size-9 text-white hidden lg:block" />
      <WrIcon
        :name="data.twoByTwo.items?.[0]?.icon || 'streamline:ticket-1'"
        :class="['size-9', isLight ? 'text-[#613FAA]' : 'text-white']"
        class="lg:hidden"
      />
    </template>
    <template #icon-step-2>
      <WrIcon name="streamline:ai-edit-spark" class="size-9 text-white hidden lg:block" />
      <WrIcon
        :name="data.twoByTwo.items?.[1]?.icon || 'streamline:ai-edit-spark'"
        :class="['size-9', isLight ? 'text-[#613FAA]' : 'text-white']"
        class="lg:hidden"
      />
    </template>
    <template #icon-step-3>
      <WrIcon name="streamline:chat-bubble-square-warning" class="size-9 text-white hidden lg:block" />
      <WrIcon
        :name="data.twoByTwo.items?.[2]?.icon || 'streamline:chat-bubble-square-warning'"
        :class="['size-9', isLight ? 'text-[#613FAA]' : 'text-white']"
        class="lg:hidden"
      />
    </template>
    <template #icon-step-4>
      <WrIcon name="streamline:code-monitor-2" class="size-9 text-white hidden lg:block" />
      <WrIcon
        :name="data.twoByTwo.items?.[3]?.icon || 'streamline:code-monitor-2'"
        :class="['size-9', isLight ? 'text-[#613FAA]' : 'text-white']"
        class="lg:hidden"
      />
    </template>
  </WrDesignTwoByTwoCardsSection>
 </div>  

  <!-- T6: Image Content (Why Choose Us) -->
  <section v-if="data.why_choose_us" class="section-spacing dark:bg-none relative z-10">
    <div class="container-wrapper-xl">
      <WrImageContent
        header="Let's start together"
        src="/images/why-chose-us.webp"
        height="360"
        width="466"
        :whyChooseUs="data.why_choose_us"
        @open-modal="openModal"
      />
    </div>
  </section>

  <!-- A8: CTA Process Steps -->
  <WrDesignProcessSteps
    v-if="data.cta"
    :heading="data.cta.heading"
    :subcopy="data.cta.subcopy"
    :steps="ctaSteps"
    cta-text="Get a Demo"
    :cta-link="null"
    @cta-clicked="openModal"
  />

  <!-- ============================================================ -->
  <!-- Zone 6: PROMISES / OUTCOMES                                   -->
  <!-- ============================================================ -->

  <!-- T7: Our Promises -->
  <section v-if="data.our_promises" class="section-spacing dark:bg-none relative z-10">
    <div class="container-wrapper-xl">
      <WrOurPromiseSection :data="data.our_promises" />
    </div>
  </section>

  <!-- A9: Outcomes -->
  <WrDesignLevelUpYourCreativityWithAi
    v-if="data.outcomes"
    :heading="data.outcomes.heading"
    :subcopy="data.outcomes.subcopy"
    :cards="outcomeCards"
    cta-text="See the Impact"
    :cta-link="null"
    @cta-clicked="openModal"
    icon-size="40px"
  />

  <!-- ============================================================ -->
  <!-- Zone 7: SOCIAL PROOF / TRUST                                  -->
  <!-- ============================================================ -->

  <!-- T8: Our Capabilities -->
  <WrOurServiceSection v-if="!data.hero" />

  <!-- T9: Selected Projects -->
  <section v-if="!data.hero" class="relative z-10 section-spacing dark:bg-none overflow-visible order-9">
    <div class="container-wrapper-lg w-full relative">
      <div
        class="hidden dark:block opacity-30 md:size-[764px] rounded-full bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] absolute -top-[30%] -left-[67%]"
      ></div>
      <div
        class="hidden dark:block opacity-50 size-[190px] rounded-full bg-[image:var(--bg-circle-top-light)] absolute -bottom-[15%] right-0 top-0"
      ></div>
      <WrSelectedProject />
    </div>
  </section>

  <!-- A10: FAQ -->
  <WrDesignFaqsSection
    v-if="data.faq && data.faq.items"
    :heading="data.faq.heading"
    :suffix="data.faq.suffix"
    :subtext="data.faq.subtext"
    :filter-tags="faqFilterTags"
    :faq-items="faqItems"
    :cta-text="data.faq.ctaText"
    :cta-link="null"
    @cta-clicked="openModal"
  />

  <!-- ============================================================ -->
  <!-- Zone 8: CLOSE / FINAL CREDIBILITY                             -->
  <!-- ============================================================ -->

  <!-- T10: Testimonials -->
  <section class="relative section-spacing dark:bg-none overflow-hidden z-20">
    <div class="container-wrapper-xl">
      <h2 class="text-center mb-11">
        <WrGradientText text="Trusted for excellence and efficiency" />
        <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
          WebReinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </h2>
      <WrTestimonialCardCarousel class="mb-12" />
      <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
        >View All</WrButton
      >
    </div>
  </section>

  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" />

</div>
 </main>  
</template>
<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 500px;
}
</style>

